import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";

const formatDateTimeLocal = (dateStr) => {
  if (!dateStr) return "";
  const str = typeof dateStr === "object" ? dateStr.toISOString() : String(dateStr);
  if (str.includes(" ")) {
    return str.replace(" ", "T").substring(0, 16);
  }
  if (str.includes("T")) {
    return str.substring(0, 16);
  }
  try {
    const d = new Date(str);
    if (isNaN(d.getTime())) return "";
    const tzoffset = d.getTimezoneOffset() * 60000;
    return (new Date(d.getTime() - tzoffset)).toISOString().substring(0, 16);
  } catch (e) {
    return "";
  }
};

const ProgressNotePrpForm = ({ formData, setFormData, readOnly = false }) => {
  const createDefaultRow = () => ({
    id: Date.now() + Math.random(),
    session_date: formatDateTimeLocal(new Date()),
    symptoms: "",
    functional_progress: "",
    goal_achievement: "",
    participation: "",
    barriers: "",
    adjustments_plan: ""
  });

  // Automatically initialize with one default row if empty
  useEffect(() => {
    if (!formData.details || formData.details.length === 0) {
      setFormData(prev => ({
        ...prev,
        details: [createDefaultRow()]
      }));
    }
  }, [formData.details, setFormData]);

  const addRow = () => {
    setFormData(prev => ({
      ...prev,
      details: [...(prev.details || []), createDefaultRow()]
    }));
  };

  const removeRow = (id) => {
    if ((formData.details || []).length <= 1) return;
    if (readOnly) return;

    Swal.fire({
      title: "Delete this session entry?",
      text: "This entry will be removed.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it",
    }).then((res) => {
      if (res.isConfirmed) {
        setFormData(prev => ({
          ...prev,
          details: prev.details.filter((r) => r.id !== id)
        }));
      }
    });
  };

  const handleRowChange = (id, field, value) => {
    if (readOnly) return;
    setFormData(prev => ({
      ...prev,
      details: prev.details.map((r) => (r.id === id ? { ...r, [field]: value } : r))
    }));
  };

  // Convert details array to renderable format (ensuring datetime-local compatibility)
  const rows = (formData.details || []).map(d => ({
    ...d,
    session_date: formatDateTimeLocal(d.session_date)
  }));

  return (
    <div className="card-body">
      <div className="col-12 mb-3">
        <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-3 px-3 py-1">
          Progress Note — Sessions List
        </small>
      </div>

      {rows.map((row, index) => (
        <div
          key={row.id}
          className="border rounded-4 p-4 mb-4 shadow-sm bg-light-subtle position-relative"
        >
          <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
            <h6 className="mb-0 text-primary fw-bold">Session #{index + 1}</h6>
            {!readOnly && rows.length > 1 && (
              <div>
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm"
                  onClick={() => removeRow(row.id)}
                >
                  <i className="ri-delete-bin-line me-1"></i> Remove Session
                </button>
              </div>
            )}
          </div>

          <div className="row g-3">
            {/* Session Date */}
            <div className="col-md-4">
              <label className="form-label text-secondary small fw-bold">Session Date <span className="text-danger">*</span></label>
              <input
                type="datetime-local"
                className="form-control"
                value={row.session_date}
                onChange={(e) => handleRowChange(row.id, "session_date", e.target.value)}
                disabled={readOnly}
              />
            </div>

            {/* Goal Achievement (%) */}
            <div className="col-md-4">
              <label className="form-label text-secondary small fw-bold">Goal Achievement (%)</label>
              <input
                type="text"
                className="form-control"
                placeholder="e.g. 80%"
                value={row.goal_achievement || ""}
                onChange={(e) => handleRowChange(row.id, "goal_achievement", e.target.value)}
                disabled={readOnly}
              />
            </div>

            {/* Symptoms */}
            <div className="col-md-6">
              <label className="form-label text-secondary small fw-bold">Symptoms</label>
              <textarea
                rows="2"
                className="form-control"
                placeholder="Describe symptoms observed"
                value={row.symptoms || ""}
                onChange={(e) => handleRowChange(row.id, "symptoms", e.target.value)}
                disabled={readOnly}
              ></textarea>
            </div>

            {/* Functional Progress */}
            <div className="col-md-6">
              <label className="form-label text-secondary small fw-bold">Functional Progress</label>
              <textarea
                rows="2"
                className="form-control"
                placeholder="Describe functional progress made"
                value={row.functional_progress || ""}
                onChange={(e) => handleRowChange(row.id, "functional_progress", e.target.value)}
                disabled={readOnly}
              ></textarea>
            </div>

            {/* Participation */}
            <div className="col-md-6">
              <label className="form-label text-secondary small fw-bold">Participation</label>
              <textarea
                rows="2"
                className="form-control"
                placeholder="Describe level of participation"
                value={row.participation || ""}
                onChange={(e) => handleRowChange(row.id, "participation", e.target.value)}
                disabled={readOnly}
              ></textarea>
            </div>

            {/* Barriers */}
            <div className="col-md-6">
              <label className="form-label text-secondary small fw-bold">Barriers</label>
              <textarea
                rows="2"
                className="form-control"
                placeholder="Identify barriers encountered"
                value={row.barriers || ""}
                onChange={(e) => handleRowChange(row.id, "barriers", e.target.value)}
                disabled={readOnly}
              ></textarea>
            </div>

            {/* Adjustments / Plan */}
            <div className="col-12">
              <label className="form-label text-secondary small fw-bold">Adjustments / Plan</label>
              <textarea
                rows="2"
                className="form-control"
                placeholder="Describe any adjustments to the rehabilitation plan"
                value={row.adjustments_plan || ""}
                onChange={(e) => handleRowChange(row.id, "adjustments_plan", e.target.value)}
                disabled={readOnly}
              ></textarea>
            </div>
          </div>
        </div>
      ))}

      {!readOnly && (
        <div className="col-12 mt-3">
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={addRow}
          >
            <i className="ri-add-line me-1"></i> Add Another Session Note
          </button>
        </div>
      )}
    </div>
  );
};

export default ProgressNotePrpForm;
